import { defineStore } from 'pinia'
import { HOST, API } from '@/utils/http'
import { toast } from '@/components/ui/toast/use-toast'
import { useOrgStore } from '@/stores/org'

type ProjectId = number | string

interface BoundaryPayload {
  projectId: ProjectId
  geometry?: unknown
  color?: string | null
  placeDetails?: unknown
}

const orgOf = () => useOrgStore().selectedOrg || useOrgStore().current?.organization_id

export const useBoundaryStore = defineStore('boundary', {
  state: () => ({
    boundaries: { type: 'FeatureCollection', features: [] } as { type: string, features: any[] },
    currentBoundary: null as any,
    isLoading: false,
    hoveredBoundaryProjectId: null as number | null,
  }),

  getters: {
    getPlaceDetails: state => (projectId: ProjectId) => {
      if (!state.boundaries || !state.boundaries.features) return null
      const boundary = state.boundaries.features.find(
        f => f.properties.project_id === projectId
      )
      return boundary?.properties?.place_details || null
    },
  },

  actions: {
    setHoveredBoundaryProjectId(projectId: number | null) {
      this.hoveredBoundaryProjectId = projectId
    },

    async fetchBoundaries({ west, south, east, north, zoom }: { west: number, south: number, east: number, north: number, zoom: number }) {
      const orgId = orgOf()
      if (!orgId) return

      this.isLoading = true
      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/org/${orgId}/project/boundaries`,
          params: { west, south, east, north, zoom },
        })
        this.boundaries = response.data
      } catch (e) {
        console.error('Failed to fetch boundaries:', e)
      } finally {
        this.isLoading = false
      }
    },

    // Bulk lookup by ids for the project list previews. Returns the features instead
    // of storing them, so the map's viewport-scoped boundaries stay untouched.
    async fetchBoundariesByIds(projectIds: ProjectId[]): Promise<any[]> {
      const orgId = orgOf()
      if (!orgId || !projectIds?.length) return []

      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/org/${orgId}/project/boundaries`,
          // Bounds are required by the endpoint but ignored when project_ids is set.
          params: { west: -180, south: -90, east: 180, north: 90, zoom: 8, project_ids: projectIds.join(',') },
        })
        return response.data?.features || []
      } catch (e) {
        console.error('Failed to fetch project boundaries:', e)
        return []
      }
    },

    async fetchProjectBoundary(projectId: ProjectId) {
      const orgId = orgOf()
      if (!orgId) return null

      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/org/${orgId}/project/${projectId}/boundary`,
        })
        this.currentBoundary = response.data
        return response.data
      } catch (e: any) {
        if (e.response?.status === 404) {
          this.currentBoundary = null
          return null
        }
        console.error('Failed to fetch project boundary:', e)
        return null
      }
    },

    async createBoundary({ projectId, geometry, color, placeDetails }: BoundaryPayload): Promise<any> {
      const orgId = orgOf()
      if (!orgId) return null

      try {
        const response = await API({
          method: 'POST',
          url: `${HOST}/org/${orgId}/project/${projectId}/boundary`,
          data: {
            geometry: geometry || null,
            color: color || '#0059ff',
            place_details: placeDetails || null,
          },
        })
        this.currentBoundary = response.data
        return response.data
      } catch (e: any) {
        if (e.response?.status === 409) {
          // Boundary already exists, update instead
          return await this.updateBoundary({ projectId, geometry, color, placeDetails })
        }
        const message = e.response?.data?.detail || "Can't create boundary"
        toast({ title: message, variant: 'destructive' })
        return null
      }
    },

    async updateBoundary({ projectId, geometry, color, placeDetails }: BoundaryPayload): Promise<any> {
      const orgId = orgOf()
      if (!orgId) return null

      try {
        const response = await API({
          method: 'PUT',
          url: `${HOST}/org/${orgId}/project/${projectId}/boundary`,
          data: {
            geometry: geometry || null,
            color: color || null,
            place_details: placeDetails || null,
          },
        })
        this.currentBoundary = response.data
        return response.data
      } catch (e: any) {
        const message = e.response?.data?.detail || "Can't update boundary"
        toast({ title: message, variant: 'destructive' })
        return null
      }
    },

    async deleteBoundary(projectId: ProjectId) {
      const orgId = orgOf()
      if (!orgId) return false

      try {
        await API({
          method: 'DELETE',
          url: `${HOST}/org/${orgId}/project/${projectId}/boundary`,
        })
        this.currentBoundary = null
        return true
      } catch (e: any) {
        const message = e.response?.data?.detail || "Can't delete boundary"
        toast({ title: message, variant: 'destructive' })
        return false
      }
    },
  },
})
